import Vue from 'vue';
import VueRouter from 'vue-router';
import CompSysConfigurator from './components/page-config.vue';
import CompTimelog from './components/page-timelog.vue';
import CompBacklog from './components/page-backlog.vue';
import CompPullRequests from './components/page-pull-requests.vue';
import CompTeamDashboard from './components/page-team-dashboard.vue';
import CompTeamInsights from './components/page-team-insights.vue';
import CompProfile from './components/page-profile.vue';
import CompPlans from './components/page-plans.vue';
import CompRepoManager from './components/page-repo-manager.vue';
import CompLanding from './components/page-landing.vue';
import CompSignIn from './components/page-sign-in.vue';
import CompSignUp from './components/page-sign-up.vue';
import CompAuthCallback from './components/page-auth-callback.vue';
import CompDiscussions from './components/page-discussions.vue';
import CompLegal from './components/page-legal.vue';
import CompWatch from './components/page-watch.vue';
import store from './store';

Vue.use(VueRouter);

const DEFAULT_SEO_TITLE = 'VelocyTrack — Engineering Cockpit, Time Tracking & Multi-Repo Backlog';
const DEFAULT_SEO_DESC = 'VelocyTrack is the developer-first engineering cockpit combining time tracking, sprint capacity, cross-repository pull requests, and branch hygiene directly into your backlog.';

const routes = [
    {
        path: '/',
        name: "landing",
        component: CompLanding,
        meta: {
            allowsAnonymouse: true,
            seoTitle: DEFAULT_SEO_TITLE,
            seoDescription: DEFAULT_SEO_DESC
        }
    },
    {
        path: '/demo',
        name: "demo",
        alias: ['/instant-demo', '/try-demo'],
        component: CompLanding,
        meta: {
            allowsAnonymouse: true,
            autoLaunchDemo: true,
            seoTitle: 'Interactive Sandbox Demo — VelocyTrack Engineering Cockpit',
            seoDescription: 'Try VelocyTrack live in your browser with zero signup. Explore multi-persona sprint backlogs, developer time tracking, sprint capacity, and multi-repo pull request radar.'
        }
    },
    {
        path: '/watch',
        name: "watch",
        component: CompWatch,
        meta: {
            allowsAnonymouse: true,
            seoTitle: 'Watch Product Demo Walkthrough — VelocyTrack Engineering Cockpit',
            seoDescription: 'Watch the narrated 4-minute product walkthrough of VelocyTrack: fast sprint time logging, automated holiday capacity, and multi-repo pull request radar for Azure DevOps.'
        }
    },
    { path: '/demo-video',                   redirect: '/watch' },
    {
        path: '/sign-in',
        name: "signin",
        component: CompSignIn,
        meta: {
            allowsAnonymouse: true,
            noindex: true,
            seoTitle: 'Sign In — VelocyTrack Engineering Cockpit',
            seoDescription: 'Sign in to your VelocyTrack workspace using Google, Microsoft, GitHub, GitLab, LinkedIn, or your corporate credentials.'
        }
    },
    { path: '/sign-up/:token',              name: "signup",  component: CompSignUp,  meta: { allowsAnonymouse: true, noindex: true } },
    { path: '/auth-callback',               name: "auth-callback", component: CompAuthCallback, meta: { allowsAnonymouse: true, noindex: true } },
    {
        path: '/terms',
        name: "terms",
        component: CompLegal,
        meta: {
            allowsAnonymouse: true,
            seoTitle: 'Terms of Service — VelocyTrack',
            seoDescription: 'Read the VelocyTrack Terms of Service governing platform access, subscription tiers, and enterprise data security.'
        }
    },
    { path: '/terms-of-service',            redirect: '/terms' },
    { path: '/legal',                       redirect: '/terms' },
    {
        path: '/privacy',
        name: "privacy",
        component: CompLegal,
        meta: {
            allowsAnonymouse: true,
            seoTitle: 'Privacy Policy & Zero-Clone Architecture — VelocyTrack',
            seoDescription: 'Learn how VelocyTrack protects engineering metadata with AES-256 encryption and a strict zero-source-code-clone privacy architecture.'
        }
    },
    { path: '/privacy-policy',              redirect: '/privacy' },
    {
        path: '/acceptable-use',
        name: "acceptable-use",
        component: CompLegal,
        meta: {
            allowsAnonymouse: true,
            seoTitle: 'Acceptable Use & Security Policy — VelocyTrack',
            seoDescription: 'Review the VelocyTrack Acceptable Use Policy and platform security guidelines for connected DevOps repositories.'
        }
    },
    { path: '/config',                 name: "config", component: CompSysConfigurator },
    { path: '/account-config',         redirect: '/config' },
    { path: '/profile',                name: "profile", component: CompProfile },
    {
        path: '/plans',
        name: "plans",
        component: CompPlans,
        meta: {
            seoTitle: 'Plans & Open Beta Pricing — VelocyTrack',
            seoDescription: 'Compare VelocyTrack Free, Pro, and Team tiers. Unlock 100% free Pro access during the open beta plus 50% off year one for founding teams.'
        }
    },
    { path: '/timelog',                name: "timelog", alias: '/time-manager', component: CompTimelog },
    { path: '/time-manager',           redirect: '/timelog' },
    { path: '/discussions',            name: "discussions", alias: ['/inbox', '/correspondence'], component: CompDiscussions },
    { path: '/inbox',                  redirect: '/discussions' },
    { path: '/correspondence',         redirect: '/discussions' },
    { path: '/backlog',                name: "backlog", alias: '/task-manager', component: CompBacklog, meta: { requiresPlan: 1 } },
    { path: '/task-manager',           redirect: '/backlog' },
    { path: '/pull-requests',          name: "pull-requests", alias: '/pr-manager', component: CompPullRequests, meta: { requiresPlan: 1 } },
    { path: '/pr-manager',             redirect: '/pull-requests' },
    { path: '/branches',               name: "branches", alias: '/repo-manager', component: CompRepoManager, meta: { requiresPlan: 1 } },
    { path: '/repo-manager',           redirect: '/branches' },
    {
        path: '/team-insights',
        name: "team-insights",
        alias: ['/subordinates-report', '/subordinates'],
        component: CompTeamInsights,
        meta: {
            requiresPlan: 2,
            seoTitle: 'Team Insights — VelocyTrack',
            seoDescription: 'Squad member profiles, monthly capacity pacing, worktype distribution, and timesheet reminders.'
        }
    },
    {
        path: '/team-dashboard',
        name: "team-dashboard",
        alias: ['/team'],
        component: CompTeamDashboard,
        meta: {
            requiresPlan: 2,
            seoTitle: 'Team Dashboard — VelocyTrack',
            seoDescription: 'Squad sprint deliverables, capacity burn-down, and absence schedule tracking.'
        }
    },
    { path: '/subordinates-report',    redirect: '/team-insights' },
    { path: '/subordinates',           redirect: '/team-insights' },
    { path: '/team',                   redirect: '/team-dashboard' },
    { path: '/team-capacity',          redirect: to => ({ path: '/team-dashboard', query: { ...to.query, tab: 'capacity' } }) }
];

const router = new VueRouter({
    mode: 'history',
    routes,
    scrollBehavior(to, from, savedPosition) {
        if (savedPosition) {
            return savedPosition;
        }
        if (to.hash) {
            return { selector: to.hash };
        }
        return { x: 0, y: 0 };
    }
});

router.beforeEach((to, from, next) => {
    // Serve static SEO guide articles directly if SPA fallback intercepted an extensionless /guides/* path
    if (to.path && to.path.startsWith('/guides/') && typeof window !== 'undefined') {
        const cleanGuidePath = to.path.replace(/\/$/, '');
        if (!cleanGuidePath.endsWith('.html')) {
            window.location.replace(`${cleanGuidePath}.html`);
            return;
        }
    }

    // Gracefully handle legacy or email links containing a hash while in history mode
    if (to.path === '/' && typeof window !== 'undefined' && window.location.hash && window.location.hash.startsWith('#/')) {
        const hashTarget = window.location.hash.substring(1);
        try {
            window.history.replaceState(null, '', window.location.pathname);
        } catch (e) {
            // ignore
        }
        return next(hashTarget);
    }

    // Allow immediate transit to landing or destination when actively exiting demo session
    if (store.state.isExitingDemo) {
        return next();
    }

    // Redirect authenticated users away from public auth/landing pages to their main dashboard
    if ((to.path === '/' || to.meta.autoLaunchDemo || to.path === '/sign-in' || to.path.startsWith('/sign-up')) && store.state.isAuthenticated) {
        return next('/timelog');
    }

    if (to.meta.allowsAnonymouse || store.state.isAuthenticated) {
        next();
    } else {
        next('/'); 
    }
});

router.afterEach((to) => {
    if (typeof window !== 'undefined') {
        try {
            const q = to.query || {};
            const utmSource = (q.utm_source || q.ref || q.source || '').toString().trim();
            const utmMedium = (q.utm_medium || '').toString().trim();
            const utmCampaign = (q.utm_campaign || '').toString().trim();
            let attribution = '';
            if (utmSource) {
                const details = [utmMedium, utmCampaign].filter(Boolean).join(' / ');
                attribution = details ? `${utmSource} (${details})` : utmSource;
            } else if (!sessionStorage.getItem('vt_attribution') && typeof document !== 'undefined' && document.referrer) {
                try {
                    const refHost = new URL(document.referrer).hostname.toLowerCase().replace(/^www\./, '');
                    if (refHost && refHost !== 'velocytrack.com' && !refHost.endsWith('.velocytrack.com') && refHost !== 'localhost' && refHost !== '127.0.0.1') {
                        attribution = `referrer:${refHost}`;
                    }
                } catch (e) {
                    // ignore invalid referrer URL
                }
            }
            if (attribution) {
                const capped = attribution.substring(0, 150);
                sessionStorage.setItem('vt_attribution', capped);
                localStorage.setItem('vt_attribution', capped);
            }
        } catch (e) {
            // ignore storage errors
        }
    }
    if (typeof document !== 'undefined') {
        const seoTitle = (to.meta && to.meta.seoTitle) || DEFAULT_SEO_TITLE;
        const seoDesc = (to.meta && to.meta.seoDescription) || DEFAULT_SEO_DESC;
        document.title = seoTitle;

        const descEl = document.querySelector('meta[name="description"]');
        if (descEl) {
            descEl.setAttribute('content', seoDesc);
        }
        const ogTitleEl = document.querySelector('meta[property="og:title"]');
        if (ogTitleEl) {
            ogTitleEl.setAttribute('content', seoTitle);
        }
        const ogDescEl = document.querySelector('meta[property="og:description"]');
        if (ogDescEl) {
            ogDescEl.setAttribute('content', seoDesc);
        }

        let canonicalEl = document.querySelector('link[rel="canonical"]');
        if (!canonicalEl) {
            canonicalEl = document.createElement('link');
            canonicalEl.setAttribute('rel', 'canonical');
            document.head.appendChild(canonicalEl);
        }
        const origin = window.location.origin
            .replace(/^http:\/\/(www\.)?(beta\.)?velocytrack\.com$/i, 'https://$2velocytrack.com')
            .replace(/^https:\/\/www\.velocytrack\.com$/i, 'https://velocytrack.com');
        const canonicalUrl = `${origin}${to.path}`;
        canonicalEl.setAttribute('href', canonicalUrl);

        const ogUrlEl = document.querySelector('meta[property="og:url"]');
        if (ogUrlEl) {
            ogUrlEl.setAttribute('content', canonicalUrl);
        }

        let robotsEl = document.querySelector('meta[name="robots"]');
        if (!robotsEl) {
            robotsEl = document.createElement('meta');
            robotsEl.setAttribute('name', 'robots');
            document.head.appendChild(robotsEl);
        }
        const shouldNoIndex = !!(to.meta && to.meta.noindex) || !(to.meta && to.meta.allowsAnonymouse);
        robotsEl.setAttribute('content', shouldNoIndex ? 'noindex, follow' : 'index, follow');
    }
});

export default router;
